import { Component, Input, Output, EventEmitter, HostListener, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RequestsV2ImplService } from 'src/app/request-v2/services/requests-v2.impl.service';
import { StatusType } from '../../../models/request.model';

interface DropdownPosition {
    top: number;
    left: number;
}

@Component({
    selector: 'app-status-select',
    templateUrl: './status-select.component.html',
    styleUrls: ['./status-select.component.scss'],
})
export class StatusSelectComponent implements OnChanges {
    @Input() statuses: StatusType[] = [];

    @Input() currentStatus: StatusType;

    @Input() requestId: string;

    @Output() statusChange = new EventEmitter<{ requestId: string; statusId: string; status: StatusType }>();

    isOpen = false;

    dropdownPosition: DropdownPosition;

    isLoading = false;

    previousStatus: StatusType;

    constructor(
        private elementRef: ElementRef,
        private requestsService: RequestsV2ImplService,
        public translate: TranslateService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.currentStatus && changes.currentStatus.currentValue) {
            this.previousStatus = { ...changes.currentStatus.currentValue };
        }
    }

    toggleDropdown(event: Event): void {
        if (this.isLoading) return;

        event.stopPropagation();
        this.isOpen = !this.isOpen;

        if (this.isOpen) {
            this.calculateDropdownPosition();
        }
    }

    calculateDropdownPosition(): void {
        const badgeElement = this.elementRef.nativeElement.querySelector('.status-badge');
        if (badgeElement) {
            const rect = badgeElement.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            const titleHeight = 25;
            const optionHeight = 32;
            const containerPadding = 32;
            const estimatedDropdownHeight = Math.min(220, this.statuses.length * optionHeight) + titleHeight + containerPadding;
            const dropdownWidth = 200;

            let top = rect.bottom + 8;
            let { left } = rect;

            if (top + estimatedDropdownHeight > viewportHeight) {
                top = rect.top - estimatedDropdownHeight - 8;
            }

            if (left + dropdownWidth > viewportWidth) {
                left = rect.right - dropdownWidth;
            }

            if (left < 8) {
                left = 8;
            }

            this.dropdownPosition = {
                top,
                left,
            };
        }
    }

    selectStatus(status: StatusType, event: Event): void {
        event.stopPropagation();
        if (this.currentStatus._id !== status._id && !this.isLoading) {
            this.isLoading = true;
            const previousStatus = this.currentStatus;
            this.currentStatus = status;

            this.requestsService.updateRequestStatus(this.requestId, status._id).subscribe(
                (response) => {
                    this.isLoading = false;
                    this.statusChange.emit({
                        requestId: this.requestId,
                        statusId: status._id,
                        status,
                    });
                },
                (error) => {
                    this.isLoading = false;
                    this.currentStatus = previousStatus;
                    console.error('Error updating status:', error);
                },
            );
        }
        this.isOpen = false;
    }

    @HostListener('document:click', ['$event'])
    clickOutside(event: Event): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.isOpen = false;
        }
    }

    @HostListener('window:scroll', [])
    onWindowScroll(): void {
        if (this.isOpen) {
            this.calculateDropdownPosition();
        }
    }

    @HostListener('window:resize', [])
    onWindowResize(): void {
        if (this.isOpen) {
            this.calculateDropdownPosition();
        }
    }

    getChipBackgroundColor(color: string, opacity = 0.3): string {
        if (!color) return 'rgba(0, 0, 0, 0.05)';

        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
}
